import { useState, useEffect, Fragment, useCallback } from "react";
import moment from "moment";
import toast from "react-hot-toast";
import { Spinner } from "@chakra-ui/react";
import ReactSelect from "react-select";


import MapSelect from "../../utils/MapSelect";
import { highPriorityKeys, Loading } from "../../utils/constants";
import APIService from "../../utils/api.service";
import DatePickerHook from "../../utils/DatePickerHook";
import { preventPasteNegative, validateIfFloat, formatAmountValueToString, toTitleCase, validateIfInteger, useEstimateCost } from "../../utils/LogicHelper";
import { useGetVehiclesQuery, useUpdateTripQuoteMutation } from "../../store/api.slice";
import { quoteStyle } from "../../utils/CustomStyles";
import VehicleSelectModal from "../../utils/VehicleSelectModal";

const Quote = ({ tripData }) => {

  const [form, setForm] = useState("delivery");
  const [extras, setExtras] = useState({});
  const [quoteInfo, setQuoteInfo] = useState("");
  const [tripTypes, setTripTypes] = useState([]);
  const [description, setDescription] = useState("");
  const [phoneError, setPhoneError] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showExtras, setShowExtras] = useState(false);
  const [showMoreExtras, setShowMoreExtras] = useState(false);
  const [isCalculating, setIsCalculating] = useState(false);
  const [selectedExtras, setSelectedExtras] = useState(tripData?.extras || {});
  const [valueAddedServices, setValueAddedServices] = useState([]);
  const [pickUp, setPickUp] = useState(tripData?.source || {});
  const [type, setType] = useState(tripData?.trip_type || "");
  const [dropOff, setDropOff] = useState(tripData?.destination || {});
  const [couponCode, setCouponCode] = useState(tripData?.coupon?.code || "");
  const [vehicle, setVehicle] = useState(tripData?.category_size || []);
  const [scheduledDate, setScheduledDate] = useState(
    tripData?.scheduled_date && moment(tripData.scheduled_date).isValid()
      ? new Date(tripData.scheduled_date)
      : ""
  );
  const [email, setEmail] = useState(tripData?.user?.email || "");
  const [phone, setPhone] = useState(tripData?.user?.phone || "");
  const [estimateValue, setEstimateValue] = useState(tripData?.estimated_value || "");
  const [selectedvehicle, setSelectedVehicle] = useState(tripData?.category_size?.id || null);
  const [lastName, setLastName] = useState(tripData?.user?.last_name || "");
  const [firstName, setFirstName] = useState(tripData?.user?.first_name || "");
  const [errorMsgs, setErrorMsgs] = useState("");


  const [updateTripQuote, { isLoading: loadingQuote }] = useUpdateTripQuoteMutation();
  const { data: vehicleSizeOptions } = useGetVehiclesQuery({
    trip_type: type,
    organisation: tripData.organisation.reference
  });

  const validateDelivery = !type || !estimateValue || !pickUp || !dropOff || !selectedvehicle || !scheduledDate;
  const validatePersonal = !email || emailError || phoneError || !phone || !firstName || !lastName;


  const getOrganisationExtras = useCallback(async () => {
    try {
      const { data: response } = await APIService.getExtras(tripData.organisation.reference);
      if (response?.status === Loading.SUCCESS) {
        setValueAddedServices(response.data);
      }
    } catch (error) {
      console.error('Failed to get extras:', error);
    }
  }, [tripData.organisation.reference]);

  const getTripType = useCallback(async () => {
    try {
      const { data: response } = await APIService.getTripTypes(tripData.organisation.reference);
      if (response?.status === Loading.SUCCESS) {
        const formattedTripType = response.data.filter(
          each => !each["system_name"].startsWith("DISTRIBUTION")
        );
        setTripTypes(formattedTripType);
      }
    } catch (error) {
      console.error('Failed to get trip types:', error);
    }
  }, [tripData.organisation.reference]);

  const calculateEstimate = useEstimateCost(setQuoteInfo);

  const handleExtrasChange = useCallback(async (extras) => {
    if (!vehicle?.id) return;

    setIsCalculating(true);
    try {
      const estimatePayload = {
        trip_type: type,
        estimated_value: estimateValue,
        destination: dropOff,
        source: pickUp,
      };

      await calculateEstimate({
        vehicle,
        estimate: estimatePayload,
        tripData,
        extras
      });
    } catch (error) {
      //toast.error(error.message || "Failed to calculate estimate");
    } finally {
      setIsCalculating(false);
    }
  }, [type, estimateValue, dropOff, pickUp, vehicle, calculateEstimate, tripData]);

  const updateExtras = useCallback((value, key) => {
    const updatedExtras = { ...extras };
    const updatedSelectedExtras = { ...selectedExtras };

    if (value === null) {
      delete updatedExtras[key];
      delete updatedSelectedExtras[key];
    } else {
      updatedExtras[key] = parseInt(value);
      updatedSelectedExtras[key] = value;
    }

    setExtras(updatedExtras);
    setSelectedExtras(updatedSelectedExtras);
    // const updatedExtras = {
    //   ...extras,
    //   [key]: value ? parseInt(value) : 0
    // };


    // setExtras(updatedExtras);
    // setSelectedExtras(prev => ({
    //   ...prev,
    //   [key]: value
    // }));

    handleExtrasChange(updatedExtras);
  }, [extras, selectedExtras, handleExtrasChange]);

  const handleClose = useCallback(() => setShowModal(false), []);

  const handleEmailError = useCallback((event) => {
    const isValid = /\S+@\S+\.\S+/.test(event.target.value);
    setEmailError(isValid ? null : "Please enter a valid email");
  }, []);

  const handlePhone = useCallback((data) => {
    const phoneRegex = /^[+]?[(]?[0-9]{3}[)]?[-\]?[0-9]{3}[-\s]?[0-9]{4,7}$/;
    setPhoneError(phoneRegex.test(data) ? "" : "Please enter a valid phone number");
  }, []);

  const handleFormStep = useCallback((e) => {
    e.preventDefault();
    setForm(prev => prev === "delivery" ? "personal" : "delivery");
    window.scrollTo(0, 0);
  }, []);

  const toggleTripTypes = useCallback((e) => {
    const selectedOption = tripTypes.find(item => item.customized_name === e.customized_name);
    if (selectedOption) {
      setType(selectedOption.customized_name);
      setDescription(selectedOption.description);
    }
  }, [tripTypes]);

  // const toggleDeliveryDetails = useCallback(() => {
  //   setShowExtras(prev => !prev);
  // }, []);


  useEffect(() => {
    if (tripData?.extras && Object.keys(tripData.extras).length > 0) {
      setShowExtras(true);
      setExtras(tripData.extras);
    }
  }, [tripData]);

  useEffect(() => {
    if (!tripData?.business_tripset || Object.keys(tripData.business_tripset).length === 0) {
      getTripType();
    }
    if (
      !tripData?.business_tripset ||
      Object.keys(tripData.business_tripset).length === 0 ||
      tripData.business_tripset.is_default === true
    ) getOrganisationExtras();
  }, [tripData, getTripType, getOrganisationExtras]);

  useEffect(() => {
    const isPickupEmpty = !pickUp || Object.keys(pickUp).length === 0;
    const isDropoffEmpty = !dropOff || Object.keys(dropOff).length === 0;

    if (isPickupEmpty || isDropoffEmpty) {
      setSelectedVehicle(null);
      setVehicle(null);
      setQuoteInfo(null);
    }
  }, [pickUp, dropOff]);

  const updateCheckQuote = useCallback(async () => {
    const callbackUrl = `${window.location.origin}/${tripData.quote.reference}/`;
    const payload = {
      email,
      phone,
      extras,
      source: pickUp,
      trip_type: type,
      last_name: lastName,
      destination: dropOff,
      first_name: firstName,
      category_size: vehicle.id,
      estimated_value: estimateValue,
      scheduled_date: moment(scheduledDate).format(),
      business: tripData?.business_tripset?.business || "",
      ...(couponCode?.trim() && { coupon: couponCode })
    };

    try {
      const response = await updateTripQuote({
        id: tripData.id,
        payload,
        organisation: tripData.organisation.reference
      });

      if (response?.data?.data) {
        setQuoteInfo(response.data.data);
        toast.success(response.data.message);
        window.location.href = callbackUrl;
      } else {
        throw new Error(response?.error?.data?.message || "Unable to get trip quotation");
      }
    } catch (error) {
      setQuoteInfo("");
      toast.error(error.message);
    }
  }, [email, phone, extras, pickUp, type, lastName, dropOff, firstName, vehicle,
    estimateValue, scheduledDate, couponCode, tripData, updateTripQuote]);

  const highPriorityValueAddedServices = valueAddedServices?.filter(service =>
    highPriorityKeys.includes(service.key)
  );

  const lowPriorityValueAddedServices = valueAddedServices?.filter(service =>
    !highPriorityKeys.includes(service.key)
  );

  const handleEstimateBlur = () => {
    if (estimateValue === "" || estimateValue === ".") return;

    const numericValue = parseFloat(estimateValue);

    if (!isNaN(numericValue)) {
      const rounded = Math.round(numericValue * 100) / 100;
      setEstimateValue(rounded.toString());
    }
  };



  return (
    <>
      <section className="overflow-y overflow-x-hidden">
        <article className="w-[100%] flex justify-center items-center mt-[2rem] min-h-screen">
          <div className="w-[752px] lg:w-[750px] max-md:w-[320px] flex flex-col">
            {/* Back Button - Personal Form */}
            {form === 'personal' && (
              <div className="mb-4">
                <button
                  className="mb-4 sm:ml-[-80px] md:ml-[-5px] lg:ml-[-90px] md:mb-2 justify-center items-center gap-1.5 inline-flex"
                  onClick={handleFormStep}
                >
                  <img
                    src="https://res.cloudinary.com/dplxh8dad/image/upload/v1690287395/TruQ/arrow-left_f7nae8.svg"
                    alt="ARROW ICON"
                    width={24}
                    height={24}
                  />
                  <p className="text-black text-base font-medium font-Neue leading-snug">
                    Back
                  </p>
                </button>
              </div>
            )}

            {/* Delivery Form */}
            <form className={`flex flex-col gap-[64px] ${form === "delivery" ? "visible" : "hidden"}`}>
              <div className="flex flex-col gap-[32px] mt-[2rem]">
                {/* Trip Type Selection */}
                {(!tripData?.business_tripset || Object.keys(tripData?.business_tripset || {}).length === 0) && (
                  <div className={`flex flex-col md:flex-row justify-between w-[100%]`}>
                    <div className="flex flex-col gap-[1rem] md:w-[48%]">
                      <label className="text-[#27262F] text-base font-medium leading-snug font-Neue">
                        Select Type of Delivery
                      </label>
                      <ReactSelect
                        options={tripTypes}
                        getOptionLabel={each => `${each['customized_name']}`}
                        isClearable={false}
                        isSearchable={true}
                        maxMenuHeight={250}
                        menuPlacement="bottom"
                        name="Trip Type"
                        styles={quoteStyle}
                        placeholder={tripData?.trip_type || "Select your trip type"}
                        className="bg-white text-zinc-400 h-[54px]"
                        onChange={toggleTripTypes}
                      />
                    </div>
                    {description && (
                      <div className="flex flex-col gap-[1rem] mt-8 md:mt-0 md:w-[48%]">
                        <label className="text-[#27262F] text-base font-medium leading-snug font-Neue">
                          Delivery Type Description
                        </label>
                        <p className={`bg-[#F3F5FF] px-2  rounded border border-[#2c3891] w-[100%]  ${type === "INTERSTATE" ? "h-[140px] py-2" : "h-[54px] py-4"} text-base font-Neue leading-5 font-bold text-zinc-400 text-wrap overflow-y-auto no-scrollbar`}>
                          {type !== "INTERSTATE" ? description : (
                            <p className="text-xl">
                              Please reach out to support for interstate trips at <a href="mailto:contact@mytruq.com" className="text-gray-600 underline">contact@mytruq.com</a> or on whatsapp <a href="https://wa.me/2348144968867?text=Hello%20TruQ%20support%2C%20I%20need%20help%20with%20an%20interstate%20trip" className="text-gray-600 underline" target="_blank" rel="noopener noreferrer">+2348144968867</a>
                            </p>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                )}
                {
                  type !== "INTERSTATE" &&
                  <>
                    {/* Location Selection */}
                    <div className="flex flex-col md:flex-row justify-between w-[100%]">
                      <div className="flex flex-col gap-[1rem] md:w-[48%]">
                        <label className="text-[#27262F] text-base font-medium leading-snug font-Neue">
                          Enter Pick Up Location
                        </label>
                        <MapSelect
                          type="SOURCE"
                          placeholder="Enter Pick Up Location"
                          value={
                            pickUp && pickUp.address
                              ? { label: pickUp.address, value: pickUp }
                              : null
                          }
                          setLocation={setPickUp}
                        />
                      </div>
                      <div className="flex flex-col gap-[1rem] md:w-[48%] mt-8 md:mt-0">
                        <label className="text-[#27262F] text-base font-medium leading-snug font-Neue">
                          Enter Drop off Location
                        </label>
                        <MapSelect
                          type="DESTINATION"
                          placeholder="Enter Drop off Location"
                          value={ dropOff && dropOff.address
                            ? { label: dropOff.address, value: dropOff }
                            : null}
                          setLocation={setDropOff}
                        />
                      </div>
                    </div>

                    {/* Date and Value */}
                    <div className="flex flex-col md:flex-row justify-between w-[100%]">
                      <div className="flex flex-col gap-[1rem] md:w-[48%]">
                        <label className="text-[#27262F] text-base font-medium leading-snug font-Neue">
                          Scheduled Date
                        </label>
                        <DatePickerHook
                          placeholder="Select scheduled date"
                          selected={scheduledDate}
                          getDate={setScheduledDate}
                          className="w-full border h-[54px] p-4 bg-[#F3F5FF] border-[#4B0082] rounded-md justify-start items-start gap-6 inline-flex text-zinc-400 text-base font-normal leading-snug font-karla outline-indigo-600"
                          timeIntervals={30}
                          dateFormat="MMMM d, yyyy h:mm aa"
                          filterTime={true}
                          showYearDropdown
                          selectDropdownMode
                          withPortal
                          showTimeSelect
                          setMinDate={new Date()}
                        />
                      </div>
                      <div className="flex flex-col gap-[1rem] mt-8 md:mt-0 md:w-[48%]">
                        <label className="text-[#27262F] text-base font-medium leading-snug font-Neue">
                          Estimated Value
                        </label>
                        <input
                          className="border h-[54px] p-3 bg-[#F3F5FF] border-[#4B0082] rounded-md justify-start items-start gap-6 inline-flex text-zinc-400 text-base font-normal leading-snug font-Neue outline-indigo-600"
                          type="number"
                          placeholder="0.00"
                          value={estimateValue}
                          onChange={(e) => setEstimateValue(e.target.value)}
                          onBlur={handleEstimateBlur}
                          min="0"
                          onPaste={preventPasteNegative}
                          onKeyPress={validateIfFloat}
                        />
                      </div>
                    </div>

                    {/* Vehicle Selection */}
                    {vehicleSizeOptions?.data?.length > 0 && (
                      <div className="flex flex-row max-md:flex-col w-[100%]">
                        <div className="flex flex-col gap-[1.3rem]">
                          <label className="text-[#27262F] text-base font-medium leading-snug font-Neue">
                            Select a Vehicle
                          </label>
                          <div className="flex flex-wrap gap-[25px]">
                            {vehicleSizeOptions.data.map((item, index) => (
                              <div
                                key={index}
                                onClick={(e) => {
                                  setVehicle(item);
                                  setSelectedVehicle(item.id);
                                  setShowModal(true)
                                  handleExtrasChange(extras);
                                }}
                                className={`w-[110px] cursor-pointer py-2 px-1 rounded-2xl transition ease-in-out delay-150 hover:-translate-y-1 hover:scale-110 hover:border-black border-2 duration-300 ${(selectedvehicle === item.id) ? "border-black" : ""
                                  }`}
                              >
                                <img src={item.image} width={62} height={60} alt="fleet" className="mx-auto" />
                                <p className="text-black text-xs font-normal text-center leading-3 font-Neue mt-2">
                                  {item.category}
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                }
              </div>
            </form>

            {/* Personal Details Form */}
            <form className={`flex flex-col gap-[64px] ${form === "personal" ? "visible" : "hidden"}`}>
              <div className="flex flex-col gap-[32px] mt-[1rem] xl:mt-[3rem]">
                {/* Name Fields */}
                <div className="flex flex-row max-md:flex-col justify-between w-[100%]">
                  <div className="flex flex-col gap-[1rem] md:w-[48%]">
                    <label className="text-[#27262F] text-base font-medium leading-snug font-Neue">
                      First Name
                    </label>
                    <input
                      className="h-[54px] p-4 bg-[#F3F5FF] border-[#4B0082] rounded-md border justify-start items-start gap-6 inline-flex text-zinc-400 text-base font-medium leading-snug font-Neue outline-indigo-600"
                      type="text"
                      placeholder="Enter First Name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                  </div>
                  <div className="flex flex-col gap-[1rem] md:w-[48%]">
                    <label className="text-[#27262F] text-base font-medium leading-snug font-Neue mt-5 md:mt-0">
                      Last Name
                    </label>
                    <input
                      className="p-4 bg-[#F3F5FF] border border-[#4B0082] rounded-md justify-start items-start gap-6 inline-flex text-zinc-400 text-base font-medium leading-snug font-Neue outline-indigo-600"
                      type="text"
                      placeholder="Enter Last Name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </div>
                </div>

                {/* Contact Fields */}
                <div className="flex flex-row max-md:flex-col justify-between w-[100%]">
                  <div className="flex flex-col gap-[1rem] md:w-[48%]">
                    <label className="text-[#27262F] text-base font-medium leading-snug font-Neue">
                      Email Address
                    </label>
                    <input
                      className="p-4 bg-[#F3F5FF] border border-[#4B0082] rounded-md justify-start items-start gap-6 inline-flex text-zinc-400 text-base font-medium leading-snug font-Neue outline-indigo-600"
                      type="email"
                      placeholder="Enter Email Address"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value.toLowerCase());
                        handleEmailError(e);
                      }}
                    />
                    {emailError && (
                      <p className="mt-1 text-left text-red-500 font-Neue">
                        {emailError}
                      </p>
                    )}
                  </div>
                  <div className="flex flex-col gap-[1rem] md:w-[48%]">
                    <label className="text-[#27262F] text-base font-medium leading-snug font-Neue mt-5 md:mt-0">
                      Phone Number
                    </label>
                    <input
                      className="p-4 bg-[#F3F5FF] border border-[#4B0082] rounded-md justify-start items-start gap-6 inline-flex text-zinc-400 text-base font-medium leading-snug font-Neue outline-indigo-600"
                      type="tel"
                      value={phone}
                      onChange={(e) => {
                        setPhone(e.target.value);
                        handlePhone(e.target.value);
                      }}
                      maxLength={16}
                      placeholder="Enter Phone Number"
                    />
                    {phoneError && (
                      <p className="mt-1 text-left text-red-500 font-Neue">
                        {phoneError}
                      </p>
                    )}
                  </div>
                </div>

                {/* Coupon and Extras */}
                <div className="flex flex-row max-md:flex-col justify-between w-[100%]">
                  <div className="flex flex-col gap-[1rem] md:w-[48%]">
                    <label className="text-[#27262F] text-base font-medium leading-snug font-Neue">
                      Enter Coupon (Optional)
                    </label>
                    <input
                      className="p-4 bg-[#F3F5FF] border border-[#4B0082] rounded-md justify-start items-start gap-6 inline-flex text-zinc-400 text-base font-normal leading-snug font-Neue outline-indigo-600"
                      type="text"
                      placeholder="Enter Coupon or Community Code"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      onPaste={preventPasteNegative}
                    />
                    {couponCode && (
                      <div className="mt-1 p-3 bg-green-50 border border-green-300 rounded">
                        <p className="text-green-700 text-sm font-medium font-Neue">
                          ✓ Coupon code added! Proceed to preview booking to see your discount.
                        </p>
                      </div>
                    )}
                  </div>

                  {valueAddedServices.length > 0 && (
                    <div className="flex flex-col gap-[1rem] mt-5 md:mt-0 md:w-[48%]">
                      <label className="text-[#27262F] text-base leading-snug font-Neue">
                        Need Value Added Services?
                      </label>
                      {/* <select
                        className="p-4 bg-[#F3F5FF] border border-[#4B0082] rounded-md text-zinc-400 text-base font-normal leading-snug font-Neue outline-indigo-600"
                        onChange={toggleDeliveryDetails}
                        value={showExtras ? "Yes" : "No"}
                      >
                        <option>No</option>
                        <option>Yes</option>
                      </select> */}
                      <div className="flex gap-4">
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="radio"
                            className="form-radio text-[#4B0082] h-4 w-4 cursor-pointer"
                            name="valueAddedServices"
                            value="No"
                            checked={!showExtras}
                            onChange={() => setShowExtras(false)}
                          />
                          <span className="ml-2 text-zinc-600">No</span>
                        </label>
                        <label className="inline-flex items-center cursor-pointer">
                          <input
                            type="radio"
                            className="form-radio text-[#4B0082] h-4 w-4 cursor-pointer"
                            name="valueAddedServices"
                            value="Yes"
                            checked={showExtras}
                            onChange={() => setShowExtras(true)}
                          />
                          <span className="ml-2 text-zinc-600">Yes</span>
                        </label>
                      </div>
                    </div>
                  )}

                </div>

                {/* Value Added Services */}
                {showExtras && (
                  <div className="flex flex-col gap-[30px]">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
                      {highPriorityValueAddedServices?.map(item => (
                        !item['name'].toLowerCase().includes('delivery') && (
                          <div key={item['key']} className=" flex flex-col h-[120px]">
                            <label className="text-[#27262F] text-base font-normal leading-snug font-karla min-h-[40px] block mb-2">
                              {item.name ? toTitleCase(item.name) : toTitleCase(item['key'])}
                            </label>
                            {/* <input
                              type="number"
                              className="p-4 my-1 bg-[#F3F5FF] border border-[#4B0082] rounded-md outline-indigo-600 w-[100%]"
                              placeholder={`Number of ${item.name ? toTitleCase(item.name) : toTitleCase(item['key'])}`}
                              value={selectedExtras[item.key] || ''}
                              onChange={event => updateExtras(event.target.value, item['key'])}
                              min="1"
                              disabled={isCalculating}
                              onPaste={preventPasteNegative}
                              onKeyPress={validateIfInteger}
                            /> */}
                            <div className="number-input-wrapper flex-1">
                              <input
                                type="number"
                                className="py-4 ps-2 pe-8 my-1 bg-[#F3F5FF] border border-[#4B0082] rounded-md outline-indigo-600 w-[100%] text-start truncate"
                                placeholder={`Number of ${item.name ? toTitleCase(item.name) : toTitleCase(item['key'])}`}
                                value={selectedExtras[item.key] || ''}
                                onChange={event => updateExtras(event.target.value, item['key'])}
                                min="0"
                                disabled={isCalculating}
                                onPaste={preventPasteNegative}
                                onKeyPress={validateIfInteger}
                              />
                              <div className="number-control">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const currentValue = parseInt(selectedExtras[item.key] || 0);
                                    updateExtras((currentValue + 1).toString(), item['key']);
                                  }}
                                  disabled={isCalculating}
                                >
                                  ^
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const currentValue = parseInt(selectedExtras[item.key] || 0);
                                    if (currentValue > 0) {
                                      updateExtras((currentValue - 1).toString(), item['key']);
                                    }
                                  }}
                                  disabled={isCalculating}
                                >
                                  v
                                </button>
                              </div>
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                )}

                {/*low priority value added services*/}
                {(lowPriorityValueAddedServices.length > 0 && showExtras) && (
                  <div className="flex flex-col gap-[1rem] mt-5 md:mt-0 md:w-[48%]">
                    <label className="text-[#27262F] text-base leading-snug font-Neue">
                      Need More Value Added Services?
                    </label>

                    <div className="flex gap-4">
                      <label className="inline-flex items-center cursor-pointer">
                        <input
                          type="radio"
                          className="form-radio text-[#4B0082] h-4 w-4 cursor-pointer"
                          name="moreValueAddedServices"
                          value="No"
                          checked={!showMoreExtras}
                          onChange={() => setShowMoreExtras(false)}
                        />
                        <span className="ml-2 text-zinc-600">No</span>
                      </label>
                      <label className="inline-flex items-center cursor-pointer">
                        <input
                          type="radio"
                          className="form-radio text-[#4B0082] h-4 w-4 cursor-pointer"
                          name="moreValueAddedServices"
                          value="Yes"
                          checked={showMoreExtras}
                          onChange={() => setShowMoreExtras(true)}
                        />
                        <span className="ml-2 text-zinc-600">Yes</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Low Priority Value Added Services */}
                {/* {showMoreExtras && (
                  <div className="flex flex-col gap-[30px]">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
                      {lowPriorityValueAddedServices?.map(item => (
                        !item['name'].toLowerCase().includes('delivery') && (
                          <div key={item['key']} className=" flex flex-col h-[120px]">
                            <label className="text-[#27262F] text-base font-normal leading-snug font-karla min-h-[40px] block mb-2">
                              {item.name ? toTitleCase(item.name) : toTitleCase(item['key'])}
                            </label>
                      
                            <div className="number-input-wrapper flex-1">
                              <input
                                type="number"
                                className="py-4 ps-2 pe-8 my-1 bg-[#F3F5FF] border border-[#4B0082] rounded-md outline-indigo-600 w-[100%] text-start truncate"
                                placeholder={`Number of ${item.name ? toTitleCase(item.name) : toTitleCase(item['key'])}`}
                                value={selectedExtras[item.key] || ''}
                                onChange={event => updateExtras(event.target.value, item['key'])}
                              />
                              <div className="number-control">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const currentValue = parseInt(selectedExtras[item.key] || 0);
                                    updateExtras((currentValue + 1).toString(), item['key']);
                                  }}
                                  disabled={isCalculating}
                                >
                                  ^
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const currentValue = parseInt(selectedExtras[item.key] || 0);
                                    if (currentValue > 0) {
                                      updateExtras((currentValue - 1).toString(), item['key']);
                                    }
                                  }}
                                  disabled={isCalculating}
                                >
                                  v
                                </button>
                              </div>
                            </div>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                )} */}
                {showMoreExtras && (
                  <div className="flex flex-col gap-[30px]">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 w-full">
                      {lowPriorityValueAddedServices?.map(item => (
                        !item['name'].toLowerCase().includes('delivery') && (
                          <div key={item['key']} className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              id={item.key}
                              checked={selectedExtras.hasOwnProperty(item.key)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  updateExtras("1", item.key);
                                } else {
                                  updateExtras(null, item.key);
                                }
                              }}
                              className="w-5 h-5 accent-indigo-600 cursor-pointer"
                            />
                            <label htmlFor={item.key} className="text-[#27262F] text-base font-normal font-karla cursor-pointer">
                              {item.name ? toTitleCase(item.name) : toTitleCase(item.key)}
                            </label>
                          </div>
                        )
                      ))}
                    </div>
                  </div>
                )}


              </div>
            </form>

            {/* Footer Section */}
            <div className="my-5 md:mt-10 flex flex-col md:flex-row md:justify-between w-full">
              {/* Estimated Cost Display */}
              <div>
                {quoteInfo?.quote?.total_charge && (
                  <Fragment>
                    <p className="text-base font-medium leading-snug font-Neue">
                      Estimated Cost
                    </p>
                    <h3 className="text-[#14219E] text-4xl font-bold font-argentum">
                      {isCalculating ? (
                        <p className="text-xl">
                          Calculating estimate{" "}
                          <Spinner color="indigo.500" className="h-[20px] w-[20px]" />
                        </p>
                      ) : (
                        <>
                          {quoteInfo.quote.currency}{" "}
                          {formatAmountValueToString(quoteInfo.quote.total_charge)}
                        </>
                      )}
                    </h3>
                  </Fragment>
                )}
              </div>

              {/* Action Buttons */}
              {form === "delivery" ? (
                <button
                  className={`md:w-[250px] w-full h-12 px-16 mt-12 md:mt-4 md:py-3 ${validateDelivery || type === "INTERSTATE" || (type === "INTRASTATE" && errorMsgs === "Please reach out to support for interstate trips") ? "bg-[#808080]" : "bg-[#14219E]"
                    } rounded-md justify-center items-center gap-3 inline-flex md:float-right`}
                  onClick={handleFormStep}
                  disabled={validateDelivery || type === "INTERSTATE" || (type === "INTRASTATE" && errorMsgs === "Please reach out to support for interstate trips")}
                >
                  <p className="text-white text-base font-bold leading-snug font-Neue">
                    Continue
                  </p>
                </button>
              ) : (
                <button
                  className={`md:w-[250px] w-full h-12 px-16 mt-12 md:mt-4 md:py-3 ${validatePersonal ? "bg-[#808080]" : "bg-[#14219E]"
                    } rounded-md justify-center items-center gap-3 inline-flex md:float-right`}
                  onClick={updateCheckQuote}
                  disabled={validatePersonal}
                >
                  <p className="text-white text-base font-bold leading-snug font-Neue">
                    Preview Booking
                  </p>
                  {loadingQuote && <Spinner color="white" className="h-[15px] w-[15px]" />}
                </button>
              )}
            </div>
          </div>
        </article >

        {/* Vehicle Selection Modal */}
        < VehicleSelectModal
          selectedVehicle={vehicle}
          onClose={handleClose}
          toggleModalDisplay={showModal}
          vehicles={vehicleSizeOptions}
          estimate={{
            trip_type: type,
            estimated_value: estimateValue,
            destination: dropOff,
            source: pickUp,
          }
          }
          tripData={tripData}
          setVehicle={setVehicle}
          hideEstimatedCost={validateDelivery}
          setQuoteInformation={setQuoteInfo}
          setSelectedVehicle={setSelectedVehicle}
          errorMsgs={errorMsgs}
          setErrorMsgs={setErrorMsgs}
        />
      </section >
    </>
  );
};

export default Quote;